import { appState } from './AppState';
import { Shape } from '../types';
import { Point } from '../../models/Point';
import { Line } from '../../models/shapes/Line';
import { cnv } from '../../libs/canvas-text/src/shared/cnv';

/**
 * Compatibility layer for existing functions from a.ts
 * This allows gradual migration without breaking existing code
 */

// Shape management functions
export function getDisplayedShapes() {
  return appState.drawing.getDisplayedShapes();
}

export function getHiddenShapes() {
  return appState.drawing.getHiddenShapes();
}

export function getashapes() {
  return appState.drawing.getShapes();
}

export function setashapes(shapes: Shape[]) {
  appState.drawing.setShapes(shapes);
}

export function addShapes(shapes: Shape | Shape[]) {
  appState.drawing.addShapes(shapes);
}

export function getSelectedShapes() {
  return appState.drawing.getSelectedShapes();
}

export function getLinesIfSelected() {
  return appState.drawing.getLinesIfSelected();
}

export function selectAllShapes() {
  appState.drawing.selectAllShapes();
}

export function resetSelectedShapes() {
  appState.drawing.resetSelectedShapes();
}

export function displayHiddenShapes() {
  appState.drawing.displayHiddenShapes();
}

export function hideSelectedShapes() {
  appState.drawing.hideSelectedShapes();
}

export function pushSelected() {
  appState.drawing.selectedShapes$.next(appState.drawing.getSelectedShapes());
}

export function selectIfMouseInShapeBoundary(mouse: Point): boolean {
  return appState.drawing.selectIfMouseInShapeBoundary(mouse);
}

// Event listener management
export function addToCurrentListeners(elem: any, type: string, func: any) {
  appState.ui.addEventListener(elem, type, func);
}

export function detachAllListeners() {
  appState.ui.removeAllEventListeners();
}

// Reset functions
export function reset() {
  appState.interaction.reset();
}

export function resetCpoints() {
  appState.interaction.resetCpoints();
}

// Mode management
export function getMode(): string {
  return appState.ui.getMode();
}

export function setMode(mode: string) {
  appState.ui.setMode(mode);
  if (cnv.context) {
    setCursor(cnv.context.canvas, mode);
  }
}

function setCursor(canvas: HTMLCanvasElement | null, mode: string) {
  if (!canvas) return;
  if (["select", "boundary", "networkHeating"].includes(mode)) {
    canvas.style.cursor = "pointer";
  } else if (["break"].includes(mode)) {
    canvas.style.cursor = "cell";
  } else {
    canvas.style.cursor = "crosshair";
  }
}

// Connection point functions
export function setCpoint1(point: Point) {
  appState.interaction.setCpoint1(point);
}

export function setCpoint2(point: Point) {
  appState.interaction.setCpoint2(point);
}

export function setCpointMod1(point: Point) {
  // This needs to be implemented in InteractionState
  console.warn('setCpointMod1 not yet implemented in new state structure');
}

export function setCpointMod2(point: Point) {
  // This needs to be implemented in InteractionState
  console.warn('setCpointMod2 not yet implemented in new state structure');
}

export function setCpointFinal1(point: Point) {
  // This needs to be implemented in InteractionState
  console.warn('setCpointFinal1 not yet implemented in new state structure');
}

export function setCpointFinal2(point: Point) {
  // This needs to be implemented in InteractionState
  console.warn('setCpointFinal2 not yet implemented in new state structure');
}

export function setCpointClick(string: string) {
  appState.interaction.setCpointClick(string);
}

export function setCpointInitAngleDeg(angleDeg: number) {
  appState.interaction.setCpointInitAngleDeg(angleDeg);
}

export function setCseg1(seg: Line) {
  // This needs to be implemented in InteractionState
  console.warn('setCseg1 not yet implemented in new state structure');
}

export function setCseg2(seg: Line) {
  // This needs to be implemented in InteractionState
  console.warn('setCseg2 not yet implemented in new state structure');
}

export function segCsegs(segs: Line[]) {
  // This needs to be implemented in InteractionState
  console.warn('segCsegs not yet implemented in new state structure');
}

export function assignAend(mouse: Point): Point {
  return appState.interaction.assignEnd(mouse);
}

export function setLastTouchedSegment(segWithConnectedSide: Line) {
  appState.interaction.setLastTouchedSegment(segWithConnectedSide);
}

export function getStartLastTouchedSegment(): Line | null {
  return appState.interaction.getStartLastTouchedSegment();
}

export function getEndLastTouchedSegment(): Line | null {
  return appState.interaction.getEndLastTouchedSegment();
}

// Network functions - these are complex and should be moved to a dedicated service
export function addNodesToNetwork(currentSegment: Line) {
  // For now, emit an event - this should be moved to a NetworkService
  appState.network.updateCalculations$.next({ type: 'addNodes', segment: currentSegment });
}

export function removeNodesFromNetwork() {
  // For now, emit an event - this should be moved to a NetworkService
  appState.network.updateCalculations$.next({ type: 'removeNodes' });
}

export function copyNodesFromNetwork() {
  // For now, emit an event - this should be moved to a NetworkService
  appState.network.updateCalculations$.next({ type: 'copyNodes' });
}

export function updateGraphNodesCoordinatesZoom(graph: any) {
  // For now, emit an event - this should be moved to a NetworkService
  appState.network.updateCalculations$.next({ type: 'updateCoordinates', graph });
}

export function updateGraphNodesCoordinatesPan() {
  // For now, emit an event - this should be moved to a NetworkService
  appState.network.updateCalculations$.next({ type: 'updateCoordinatesPan' });
}
